import type { BlockStateDefinition } from '@unifyapps/carbon/no-code/components/BlockDefinition';
import type { BlockType, ComponentTypeUnionType } from '@unifyapps/defs/types/block';
import _pick from 'lodash/pick';
import type {
  CommentsComponentType,
  EntityContentType,
} from '@unifyapps/defs/blocks/Comments/types';
import { lazy } from 'react';
import { getSelectedDataSourcePath } from '@unifyapps/carbon/utils/getSelectedDataSourcePath';
import { DependencyPaths } from '@unifyapps/carbon/no-code/helper/DependencyPaths';
import { importBaseRichTextEditor } from '@unifyapps/editor/editors/RichTextEditor/RichTextEditor';

const importCommentsComponent = () =>
  import(
    /* webpackChunkName: "comments-block" */
    './Comments'
  );

const Comments = lazy(importCommentsComponent);

class CommentsBlockStateDefinition implements BlockStateDefinition {
  get type(): ComponentTypeUnionType {
    return 'Comments';
  }

  getSlotOrder(block: BlockType): { order: string[] } {
    return {
      order: ['commentInput', 'commentList'],
    };
  }

  get initialStateGetter() {
    return (block: BlockType) => ({
      id: block.id,
      ..._pick(block.component as CommentsComponentType, [
        'content',
        'appearance',
        'componentType',
      ]),
    });
  }

  getImplicitDependencies(block: BlockType<CommentsComponentType>) {
    const { dataSourceId } = getSelectedDataSourcePath({
      data: (block.component.content as EntityContentType).data,
    });

    if (!dataSourceId) return;
    const paths = DependencyPaths.dataSource(dataSourceId);

    return [
      {
        id: dataSourceId,
        p: [paths.isLoading],
      },
    ];
  }

  getComponent() {
    return Comments;
  }

  preload() {
    return {
      blockPreload: importCommentsComponent,
      miscellaneous: (block?: BlockType<CommentsComponentType>) => {
        return [importBaseRichTextEditor] as unknown as (() => Promise<void>)[];
      },
    };
  }
}

export default CommentsBlockStateDefinition;
